import types from '@store/constants/libraDashboard';
import api from '@api/endpoints';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE } from '@constants';

// Accounts Actions
export const getAccounts = (params, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.GET_ACCOUNTS });
    const { data } = await api.libraDashboard.getAccounts(params);
    dispatch({ type: types.GET_ACCOUNTS_SUCCESS, payload: data });
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to fetch accounts', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.GET_ACCOUNTS_FAILURE, payload: error });
  }
};

export const createAccount = (reqData, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.CREATE_ACCOUNT });
    const { data } = await api.libraDashboard.createAccount(reqData);
    dispatch({ type: types.CREATE_ACCOUNT_SUCCESS, payload: data });
    dispatch(setAlert({ title: 'Account created successfully', status: ALERT_STATUS_TYPE.SUCCESS }));
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to create account', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.CREATE_ACCOUNT_FAILURE, payload: error });
  }
};


// Transfers Actions
export const getTransfers = (body, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.GET_TRANSFERS });
    const { data } = await api.libraDashboard.listTransfers(body);
    const items = data?.transfers ?? data?.items ?? data?.rows ?? [];
    const maxPages = data?.maxPages ?? data?.totalPages ?? data?.max_pages ?? 1;
    const totalItems = data?.totalItems ?? data?.totalCount ?? data?.total ?? 0;
    const normalised = { items, maxPages, totalItems };
    dispatch({ type: types.GET_TRANSFERS_SUCCESS, payload: normalised });
    onSuccess && onSuccess(normalised);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to fetch transfers', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.GET_TRANSFERS_FAILURE, payload: error });
  }
};

export const createTransfer = (reqData, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.CREATE_TRANSFER });
    const { data } = await api.libraDashboard.createTransfer(reqData);
    dispatch({ type: types.CREATE_TRANSFER_SUCCESS, payload: data });
    dispatch(setAlert({ title: 'Transfer created successfully', status: ALERT_STATUS_TYPE.SUCCESS }));
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to create transfer', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.CREATE_TRANSFER_FAILURE, payload: error });
  }
};

export const processTransfer = (externalId, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.PROCESS_TRANSFER });
    const { data } = await api.libraDashboard.processTransfer(externalId);
    dispatch({ type: types.PROCESS_TRANSFER_SUCCESS, payload: data });
    dispatch(setAlert({ title: 'Transfer processed successfully', status: ALERT_STATUS_TYPE.SUCCESS }));
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to process transfer', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.PROCESS_TRANSFER_FAILURE, payload: error });
  }
};


// Corporate Beneficiaries Actions
export const getCorporateBeneficiaries = (reqData, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.GET_CORPORATE_BENEFICIARIES });
    const { data } = await api.libraDashboard.getCorporateBeneficiaries(reqData);
    dispatch({ type: types.GET_CORPORATE_BENEFICIARIES_SUCCESS, payload: data });
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to fetch corporate beneficiaries', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.GET_CORPORATE_BENEFICIARIES_FAILURE, payload: error });
  }
};

export const createCorporateBeneficiary = (reqData, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.CREATE_CORPORATE_BENEFICIARY });
    const { data } = await api.libraDashboard.createCorporateBeneficiary(reqData);
    dispatch({ type: types.CREATE_CORPORATE_BENEFICIARY_SUCCESS, payload: data });
    dispatch(setAlert({ title: 'Corporate beneficiary created successfully', status: ALERT_STATUS_TYPE.SUCCESS }));
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to create corporate beneficiary', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.CREATE_CORPORATE_BENEFICIARY_FAILURE, payload: error });
  }
};

// Scheduled Transfers Actions
export const getScheduledTransfers = (reqData, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.GET_SCHEDULED_TRANSFERS });
    const { data } = await api.libraDashboard.getScheduledTransfers(reqData);
    dispatch({ type: types.GET_SCHEDULED_TRANSFERS_SUCCESS, payload: data });
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to fetch scheduled transfers', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.GET_SCHEDULED_TRANSFERS_FAILURE, payload: error });
  }
};

export const createScheduledTransfer = (reqData, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.CREATE_SCHEDULED_TRANSFER });
    const { data } = await api.libraDashboard.createScheduledTransfer(reqData);
    dispatch({ type: types.CREATE_SCHEDULED_TRANSFER_SUCCESS, payload: data });
    dispatch(setAlert({ title: 'Scheduled transfer created successfully', status: ALERT_STATUS_TYPE.SUCCESS }));
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to create scheduled transfer', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.CREATE_SCHEDULED_TRANSFER_FAILURE, payload: error });
  }
};

export const updateScheduledTransfer = (scheduledTransferId, reqData, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.UPDATE_SCHEDULED_TRANSFER });
    const { data } = await api.libraDashboard.updateScheduledTransfer(scheduledTransferId, reqData);
    dispatch({ type: types.UPDATE_SCHEDULED_TRANSFER_SUCCESS, payload: data });
    dispatch(setAlert({ title: 'Scheduled transfer updated successfully', status: ALERT_STATUS_TYPE.SUCCESS }));
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to update scheduled transfer', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.UPDATE_SCHEDULED_TRANSFER_FAILURE, payload: error });
  }
};

// Libra Dashboard Configs Actions
export const getLibraDashboardConfigs = (onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: types.GET_LIBRA_DASHBOARD_CONFIGS });
    const { data } = await api.libraDashboard.getLibraDashboardConfigs();
    dispatch({ type: types.GET_LIBRA_DASHBOARD_CONFIGS_SUCCESS, payload: data });
    onSuccess && onSuccess(data);
  } catch (error) {
    dispatch(setAlert({ title: error?.message || 'Failed to fetch libra dashboard configs', status: ALERT_STATUS_TYPE.ERROR }));
    dispatch({ type: types.GET_LIBRA_DASHBOARD_CONFIGS_FAILURE, payload: error });
  }
};
